/**
 * ColdStartFlow — Branch C onboarding: create a provisional property without cloud discovery.
 * Steps: Property Details → Equipment (optional) → Gateway → Review & Create
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  MapPin,
  Battery,
  Sun,
  Cpu,
  Zap,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { toast } from 'sonner';

type ColdStartStep = 'property' | 'equipment' | 'gateway' | 'review' | 'complete';

interface EquipmentData {
  inverters: Array<{ manufacturer: string; model: string; serial: string; capacity_kw: string }>;
  batteries: Array<{ manufacturer: string; model: string; capacity_kwh: string; quantity: string }>;
  solar: { capacity_kw: string; panel_count: string };
  generator: { manufacturer: string; model: string; present: boolean };
}

interface PropertyData {
  name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  notes: string;
}

interface CreatedProperty {
  id: string;
  name: string;
  readiness_state: string;
  address: string | null;
}

const EMPTY_EQUIPMENT: EquipmentData = {
  inverters: [{ manufacturer: '', model: '', serial: '', capacity_kw: '' }],
  batteries: [{ manufacturer: '', model: '', capacity_kwh: '', quantity: '1' }],
  solar: { capacity_kw: '', panel_count: '' },
  generator: { manufacturer: '', model: '', present: false },
};

interface ColdStartFlowProps {
  onBack: () => void;
  /** gateway_only = new install (Branch C); gateway_primary = credential recovery (Branch B) */
  dataSourceMode?: 'gateway_only' | 'gateway_primary';
}

export function ColdStartFlow({ onBack, dataSourceMode = 'gateway_only' }: ColdStartFlowProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<ColdStartStep>('property');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [property, setProperty] = useState<PropertyData>({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    notes: '',
  });

  const [equipment, setEquipment] = useState<EquipmentData>(EMPTY_EQUIPMENT);
  const [gatewayClaimCode, setGatewayClaimCode] = useState('');
  const [addGatewayLater, setAddGatewayLater] = useState(false);
  const [createdProperty, setCreatedProperty] = useState<CreatedProperty | null>(null);

  const steps: { key: ColdStartStep; label: string }[] = [
    { key: 'property', label: 'Property' },
    { key: 'equipment', label: 'Equipment' },
    { key: 'gateway', label: 'Gateway' },
    { key: 'review', label: 'Review' },
    { key: 'complete', label: 'Done' },
  ];

  const currentIndex = steps.findIndex((s) => s.key === step);

  function canProceedFromProperty(): boolean {
    return property.name.trim().length > 0;
  }

  function buildEquipmentPayload(): Record<string, unknown> | undefined {
    const hasInverter = equipment.inverters.some((i) => i.manufacturer || i.model);
    const hasBattery = equipment.batteries.some((b) => b.manufacturer || b.capacity_kwh);
    const hasSolar = !!equipment.solar.capacity_kw;
    const hasGenerator = equipment.generator.present;

    if (!hasInverter && !hasBattery && !hasSolar && !hasGenerator) return undefined;

    const payload: Record<string, unknown> = {};

    if (hasInverter) {
      payload.inverters = equipment.inverters
        .filter((i) => i.manufacturer || i.model)
        .map((i) => ({
          manufacturer: i.manufacturer || undefined,
          model: i.model || undefined,
          serial: i.serial || undefined,
          capacity_kw: i.capacity_kw ? Number(i.capacity_kw) : undefined,
        }));
    }

    if (hasBattery) {
      payload.batteries = equipment.batteries
        .filter((b) => b.manufacturer || b.capacity_kwh)
        .map((b) => ({
          manufacturer: b.manufacturer || undefined,
          model: b.model || undefined,
          capacity_kwh: b.capacity_kwh ? Number(b.capacity_kwh) : undefined,
          quantity: b.quantity ? Number(b.quantity) : 1,
        }));
    }

    if (hasSolar) {
      payload.solar = {
        capacity_kw: Number(equipment.solar.capacity_kw) || undefined,
        panel_count: equipment.solar.panel_count ? Number(equipment.solar.panel_count) : undefined,
      };
    }

    if (hasGenerator) {
      payload.generator = {
        manufacturer: equipment.generator.manufacturer || undefined,
        model: equipment.generator.model || undefined,
      };
    }

    return payload;
  }

  async function handleCreate() {
    setLoading(true);
    setError(null);

    try {
      const body: Record<string, unknown> = {
        name: property.name.trim(),
        data_source_mode: dataSourceMode,
      };

      if (property.address) body.address = property.address;
      if (property.city) body.city = property.city;
      if (property.state) body.state = property.state;
      if (property.postal_code) body.postal_code = property.postal_code;
      if (property.notes) body.notes = property.notes;

      const equipPayload = buildEquipmentPayload();
      if (equipPayload) body.equipment = equipPayload;

      const result = await invokeAuthedFunction<{
        success: boolean;
        property: CreatedProperty;
        error?: string;
      }>(supabase, 'create-provisional-property', body);

      if (!result?.success || !result.property) {
        throw new Error(result?.error || 'Failed to create property');
      }

      setCreatedProperty(result.property);
      setStep('complete');
      toast.success(`Property "${result.property.name}" created`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create property';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  function updateInverter(index: number, field: string, value: string) {
    setEquipment((prev) => ({
      ...prev,
      inverters: prev.inverters.map((inv, i) =>
        i === index ? { ...inv, [field]: value } : inv,
      ),
    }));
  }

  function addInverter() {
    setEquipment((prev) => ({
      ...prev,
      inverters: [...prev.inverters, { manufacturer: '', model: '', serial: '', capacity_kw: '' }],
    }));
  }

  function updateBattery(index: number, field: string, value: string) {
    setEquipment((prev) => ({
      ...prev,
      batteries: prev.batteries.map((bat, i) =>
        i === index ? { ...bat, [field]: value } : bat,
      ),
    }));
  }

  // ─── Step renderers ───────────────────────────────────────────────────────

  function renderPropertyStep() {
    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="prop-name">Property Name *</Label>
          <Input
            id="prop-name"
            placeholder="e.g. Smith Residence"
            value={property.name}
            onChange={(e) => setProperty((p) => ({ ...p, name: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor="prop-address">Street Address</Label>
          <Input
            id="prop-address"
            placeholder="123 Main St"
            value={property.address}
            onChange={(e) => setProperty((p) => ({ ...p, address: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="prop-city">City</Label>
            <Input
              id="prop-city"
              value={property.city}
              onChange={(e) => setProperty((p) => ({ ...p, city: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="prop-state">State</Label>
            <Input
              id="prop-state"
              placeholder="TX"
              maxLength={2}
              value={property.state}
              onChange={(e) => setProperty((p) => ({ ...p, state: e.target.value.toUpperCase() }))}
            />
          </div>
          <div>
            <Label htmlFor="prop-zip">ZIP</Label>
            <Input
              id="prop-zip"
              placeholder="78701"
              value={property.postal_code}
              onChange={(e) => setProperty((p) => ({ ...p, postal_code: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="prop-notes">Notes (optional)</Label>
          <Input
            id="prop-notes"
            placeholder="Gate code, site access notes, etc."
            value={property.notes}
            onChange={(e) => setProperty((p) => ({ ...p, notes: e.target.value }))}
          />
        </div>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button onClick={() => setStep('equipment')} disabled={!canProceedFromProperty()}>
            Next <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  function renderEquipmentStep() {
    return (
      <div className="space-y-6">
        <p className="text-sm text-wt-text-muted">
          Optional. Enter what you know about the equipment on-site. This helps with gateway commissioning later.
        </p>

        {/* Inverters */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Cpu className="h-4 w-4" /> Inverter(s)
            </Label>
            <Button variant="ghost" size="sm" onClick={addInverter}>+ Add</Button>
          </div>
          {equipment.inverters.map((inv, i) => (
            <div key={i} className="grid grid-cols-4 gap-2">
              <Input placeholder="Brand" value={inv.manufacturer} onChange={(e) => updateInverter(i, 'manufacturer', e.target.value)} />
              <Input placeholder="Model" value={inv.model} onChange={(e) => updateInverter(i, 'model', e.target.value)} />
              <Input placeholder="Serial" value={inv.serial} onChange={(e) => updateInverter(i, 'serial', e.target.value)} />
              <Input placeholder="kW" type="number" value={inv.capacity_kw} onChange={(e) => updateInverter(i, 'capacity_kw', e.target.value)} />
            </div>
          ))}
        </div>

        {/* Battery */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Battery className="h-4 w-4" /> Battery
          </Label>
          {equipment.batteries.map((bat, i) => (
            <div key={i} className="grid grid-cols-4 gap-2">
              <Input placeholder="Brand" value={bat.manufacturer} onChange={(e) => updateBattery(i, 'manufacturer', e.target.value)} />
              <Input placeholder="Model" value={bat.model} onChange={(e) => updateBattery(i, 'model', e.target.value)} />
              <Input placeholder="kWh per unit" type="number" value={bat.capacity_kwh} onChange={(e) => updateBattery(i, 'capacity_kwh', e.target.value)} />
              <Input placeholder="Qty" type="number" value={bat.quantity} onChange={(e) => updateBattery(i, 'quantity', e.target.value)} />
            </div>
          ))}
        </div>

        {/* Solar */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Sun className="h-4 w-4" /> Solar Array
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Total kW" type="number" value={equipment.solar.capacity_kw} onChange={(e) => setEquipment((prev) => ({ ...prev, solar: { ...prev.solar, capacity_kw: e.target.value } }))} />
            <Input placeholder="Panel count" type="number" value={equipment.solar.panel_count} onChange={(e) => setEquipment((prev) => ({ ...prev, solar: { ...prev.solar, panel_count: e.target.value } }))} />
          </div>
        </div>

        {/* Generator */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="gen-present"
              checked={equipment.generator.present}
              onCheckedChange={(checked) =>
                setEquipment((prev) => ({
                  ...prev,
                  generator: { ...prev.generator, present: !!checked },
                }))
              }
            />
            <Label htmlFor="gen-present" className="flex items-center gap-2">
              <Zap className="h-4 w-4" /> Generator present
            </Label>
          </div>
          {equipment.generator.present && (
            <div className="grid grid-cols-2 gap-2 ml-6">
              <Input placeholder="Make" value={equipment.generator.manufacturer} onChange={(e) => setEquipment((prev) => ({ ...prev, generator: { ...prev.generator, manufacturer: e.target.value } }))} />
              <Input placeholder="Model" value={equipment.generator.model} onChange={(e) => setEquipment((prev) => ({ ...prev, generator: { ...prev.generator, model: e.target.value } }))} />
            </div>
          )}
        </div>

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep('property')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button onClick={() => setStep('gateway')}>
            Next <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  function renderGatewayStep() {
    return (
      <div className="space-y-6">
        <p className="text-sm text-wt-text-muted">
          If you have a Watt Tower Gateway, enter the claim code now. Otherwise, you can add one later.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="claim-code">Gateway Claim Code</Label>
            <Input
              id="claim-code"
              placeholder="WT-XXXXX"
              value={gatewayClaimCode}
              onChange={(e) => {
                setGatewayClaimCode(e.target.value);
                if (e.target.value) setAddGatewayLater(false);
              }}
              disabled={addGatewayLater}
            />
            <p className="text-xs text-wt-text-muted mt-1">
              Found on the label of your gateway device.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="gateway-later"
              checked={addGatewayLater}
              onCheckedChange={(checked) => {
                setAddGatewayLater(!!checked);
                if (checked) setGatewayClaimCode('');
              }}
            />
            <Label htmlFor="gateway-later" className="text-sm text-wt-text-secondary">
              I'll add a gateway later
            </Label>
          </div>
        </div>

        {gatewayClaimCode && !addGatewayLater && (
          <Card className="border-wt-border bg-wt-surface-secondary">
            <CardContent className="p-3 text-sm text-wt-text-muted">
              Gateway linking will be available after the property is created. The claim code will be saved in your property notes for reference.
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep('equipment')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button onClick={() => setStep('review')}>
            Review <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  function renderReviewStep() {
    const equipPayload = buildEquipmentPayload();
    const hasBattery = equipment.batteries.some((b) => b.capacity_kwh);
    const totalKwh = equipment.batteries.reduce((sum, b) => {
      const cap = Number(b.capacity_kwh) || 0;
      const qty = Number(b.quantity) || 1;
      return sum + cap * qty;
    }, 0);

    return (
      <div className="space-y-4">
        <Card className="border-wt-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Property
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p><span className="text-wt-text-muted">Name:</span> {property.name}</p>
            {property.address && <p><span className="text-wt-text-muted">Address:</span> {[property.address, property.city, property.state, property.postal_code].filter(Boolean).join(', ')}</p>}
            {property.notes && <p><span className="text-wt-text-muted">Notes:</span> {property.notes}</p>}
          </CardContent>
        </Card>

        {equipPayload && (
          <Card className="border-wt-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Cpu className="h-4 w-4" /> Equipment
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {equipment.inverters.filter((i) => i.manufacturer || i.model).map((i, idx) => (
                <p key={idx}><span className="text-wt-text-muted">Inverter:</span> {[i.manufacturer, i.model, i.capacity_kw && `${i.capacity_kw} kW`].filter(Boolean).join(' ')}</p>
              ))}
              {hasBattery && <p><span className="text-wt-text-muted">Battery:</span> {totalKwh > 0 ? `${totalKwh} kWh total` : 'Details entered'}</p>}
              {equipment.solar.capacity_kw && <p><span className="text-wt-text-muted">Solar:</span> {equipment.solar.capacity_kw} kW{equipment.solar.panel_count && ` (${equipment.solar.panel_count} panels)`}</p>}
              {equipment.generator.present && <p><span className="text-wt-text-muted">Generator:</span> {[equipment.generator.manufacturer, equipment.generator.model].filter(Boolean).join(' ') || 'Yes'}</p>}
            </CardContent>
          </Card>
        )}

        <Card className="border-wt-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Setup Status</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p><span className="text-wt-text-muted">Monitoring:</span> <Badge variant="outline" className="text-xs">Pending Gateway</Badge></p>
            <p><span className="text-wt-text-muted">Mode:</span> {dataSourceMode === 'gateway_primary' ? 'Gateway primary (cloud can be restored later)' : 'Gateway only'}</p>
            <p><span className="text-wt-text-muted">Billing:</span> Not active until telemetry begins</p>
            {gatewayClaimCode && <p><span className="text-wt-text-muted">Claim code:</span> {gatewayClaimCode} (saved in notes)</p>}
            {addGatewayLater && <p><span className="text-wt-text-muted">Gateway:</span> Will be added later</p>}
          </CardContent>
        </Card>

        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep('gateway')} disabled={loading}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <Button onClick={handleCreate} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...
              </>
            ) : (
              'Create Property'
            )}
          </Button>
        </div>
      </div>
    );
  }

  function renderCompleteStep() {
    return (
      <div className="text-center space-y-6 py-8">
        <CheckCircle2 className="h-12 w-12 text-wt-green-primary mx-auto" />
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-wt-text-primary">Property Created</h2>
          <p className="text-sm text-wt-text-muted">
            <strong>{createdProperty?.name}</strong> has been added to your fleet as a provisional property.
            {dataSourceMode === 'gateway_primary'
              ? ' The gateway will be the primary monitoring source. Cloud monitoring can be restored later if credentials become available.'
              : ' Telemetry will begin once a gateway is installed and commissioned.'}
          </p>
        </div>

        <div className="flex flex-col gap-3 max-w-xs mx-auto">
          <Button onClick={() => createdProperty && navigate(`/app/property/${createdProperty.id}`, { replace: true })}>
            View Property
          </Button>
          <Button variant="outline" onClick={() => navigate('/app/fleet', { replace: true })}>
            Go to Fleet Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────────────

  const stepTitle: Record<ColdStartStep, string> = {
    property: 'Property Details',
    equipment: 'Equipment on Site',
    gateway: 'Gateway Setup',
    review: 'Review & Create',
    complete: 'Done',
  };

  return (
    <div className="max-w-2xl mx-auto">
      {step !== 'complete' && (
        <>
          {/* Progress */}
          <div className="flex items-center justify-between mb-6 text-xs text-wt-text-muted">
            {steps.filter((s) => s.key !== 'complete').map((s, i) => (
              <div
                key={s.key}
                className={cn(
                  'flex items-center gap-1',
                  i <= currentIndex ? 'text-wt-text-primary font-medium' : '',
                )}
              >
                <span className={cn(
                  'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] border',
                  i < currentIndex ? 'bg-wt-primary text-white border-wt-primary' :
                  i === currentIndex ? 'border-wt-primary text-wt-primary' :
                  'border-wt-border text-wt-text-muted',
                )}>
                  {i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            ))}
          </div>

          <Card className="border-wt-border">
            <CardHeader>
              <CardTitle>{stepTitle[step]}</CardTitle>
              <CardDescription>
                {step === 'property' && 'Enter the property name and location.'}
                {step === 'equipment' && 'Tell us what equipment is installed (all fields optional).'}
                {step === 'gateway' && 'Connect a Watt Tower Gateway for monitoring.'}
                {step === 'review' && 'Confirm the details and create this provisional property.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 'property' && renderPropertyStep()}
              {step === 'equipment' && renderEquipmentStep()}
              {step === 'gateway' && renderGatewayStep()}
              {step === 'review' && renderReviewStep()}
            </CardContent>
          </Card>
        </>
      )}

      {step === 'complete' && renderCompleteStep()}
    </div>
  );
}
